/**
 * Print-only week plan tile atom for Monthly Consultation PDF.
 */

import React from "react";
import {
  bodyFontFamily,
  border,
  charcoal,
  displayFontFamily,
  mutedGray,
} from "../pdfTheme";
import { PdfCard } from "./PdfCard";
import { PdfPill } from "./PdfPill";

/**
 * Props for one week-by-week plan tile.
 */
export interface PdfWeekTileProps {
  /** Week label (e.g. "Week 1" or "1"). */
  week: string;
  /** Stance label shown in the pill (Push, Careful, Steady, Rest). */
  stance: string;
  /** Stance pill text color (theme token). */
  stanceColor: string;
  /** Coaching / plan body for the week. */
  body: string;
}

/**
 * Soft tint background derived from a stance color (12% alpha hex suffix).
 *
 * @param color - Theme hex color such as `#15803d`
 * @returns CSS color suitable for a light pill background
 */
function tintFromColor(color: string): string {
  const trimmed = color.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) {
    return `${trimmed}1f`;
  }
  return border;
}

/**
 * One week tile with a stance pill and body coaching text.
 * Used by the week-by-week plan chapter.
 */
export const PdfWeekTile: React.FC<PdfWeekTileProps> = ({
  week,
  stance,
  stanceColor,
  body,
}) => {
  const safeWeek = typeof week === "string" ? week.trim() : "";
  const safeStance = typeof stance === "string" ? stance.trim() : "";
  const safeBody = typeof body === "string" ? body.trim() : "";
  const safeColor =
    typeof stanceColor === "string" && stanceColor.trim().length > 0
      ? stanceColor.trim()
      : mutedGray;

  const weekHeading =
    safeWeek.length === 0
      ? "Week"
      : /^week\b/i.test(safeWeek)
        ? safeWeek
        : `Week ${safeWeek}`;

  return (
    <PdfCard>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          marginBottom: 10,
          fontFamily: bodyFontFamily,
        }}
      >
        <h4
          style={{
            margin: 0,
            fontFamily: displayFontFamily,
            fontSize: 15,
            fontWeight: 700,
            color: charcoal,
            letterSpacing: "-0.01em",
          }}
        >
          {weekHeading}
        </h4>
        {safeStance.length > 0 ? (
          <PdfPill
            label={safeStance}
            color={safeColor}
            bg={tintFromColor(safeColor)}
          />
        ) : null}
      </div>
      {safeBody.length > 0 ? (
        <p
          style={{
            margin: 0,
            fontFamily: bodyFontFamily,
            fontSize: 12,
            lineHeight: 1.55,
            color: mutedGray,
          }}
        >
          {safeBody}
        </p>
      ) : null}
    </PdfCard>
  );
};
